import { useCallback, useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { AlertCircle, Check, Copy, KeyRound, Loader2, RefreshCw, ShieldCheck, Smartphone } from "lucide-react";
import { cn } from "./ui";
import { apiErrorText } from "@/lib/api";
import { onServerEvent } from "@/lib/events";
import { t } from "@/lib/i18n";
import { channelsApi, type Channel, type GatewayState, type TelegramApp } from "./inbox-api";
import { Hint, SideCard, Steps, inputCls, monoInputCls, type PanelProps } from "./channel-wizard-kit";

/* Вход в личный аккаунт Telegram через шлюз tg-gateway (MTProto): QR-код как в «Подключении устройства».
   Телефон сканирует код и подтверждает вход; если в Telegram включена двухэтапная проверка — шлюз попросит облачный
   пароль, его вводят здесь (Hub пароль не хранит). Канал создаётся на сервере сразу — состояние приходит
   живыми событиями и опросом раз в 2 секунды. */

export default function TelegramLogin({ onDone, existing }: PanelProps & { existing?: Channel | null }) {
  // ключи приложения Telegram (один раз для всей компании): пока их нет — шаг ввода вместо QR
  const [app, setApp] = useState<TelegramApp | null | undefined>(undefined);

  useEffect(() => {
    channelsApi.telegramApp().then(setApp, () => setApp(null));
  }, []);

  if (app === undefined) {
    return (
      <div className="grid flex-1 place-items-center p-8 text-muted-foreground">
        <Loader2 className="size-6 animate-spin" aria-hidden />
      </div>
    );
  }
  if (app && !app.configured) return <TelegramAppSetup app={app} onReady={setApp} />;
  return <TelegramQr onDone={onDone} existing={existing} />;
}

/** Один раз для всей компании: api_id и api_hash с my.telegram.org — Telegram требует регистрации приложения (у WhatsApp такого нет). */
function TelegramAppSetup({ app, onReady }: { app: TelegramApp; onReady: (app: TelegramApp) => void }) {
  const [apiId, setApiId] = useState("");
  const [apiHash, setApiHash] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const valid = /^\d{1,12}$/.test(apiId.trim()) && /^[0-9a-fA-F]{32}$/.test(apiHash.trim());

  const save = async () => {
    if (!valid || saving) return;
    setSaving(true);
    setError("");
    try {
      onReady(await channelsApi.setTelegramApp(Number(apiId.trim()), apiHash.trim()));
    } catch (err) {
      setError(apiErrorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid flex-1 overflow-y-auto lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="min-w-0 p-4 md:p-5" data-testid="tg-app-setup">
        <Steps active={1} items={[t("Зарегистрировать приложение (один раз)"), t("Отсканировать QR-код"), t("Готово")]} />
        <div className="mt-4 rounded-btn border border-border bg-background p-3">
          <p className="text-[12.5px] font-semibold text-foreground">{t("Telegram требует один раз зарегистрировать приложение")}</p>
          <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
            {t("В отличие от WhatsApp, Telegram пускает только зарегистрированные программы. Регистрация делается один раз на всю компанию, занимает две минуты и больше не повторяется: после неё каждый продавец подключает свой Telegram по QR-коду, как WhatsApp.")}
          </p>
          <ol className="mt-2 flex list-decimal flex-col gap-1 pl-4 text-[11.5px] leading-relaxed text-muted-foreground">
            <li>
              {t("Откройте")}{" "}
              <a href="https://my.telegram.org/apps" target="_blank" rel="noreferrer" className="font-semibold text-primary underline">
                my.telegram.org/apps
              </a>{" "}
              {t("и войдите по номеру телефона (код придёт в Telegram, не по SMS).")}
            </li>
            <li>{t("Заполните «App title» и «Short name» (например, Hub), остальное можно оставить пустым, нажмите «Create application».")}</li>
            <li>{t("Скопируйте сюда два значения со страницы: «App api_id» и «App api_hash».")}</li>
          </ol>
        </div>

        {app.canEdit ? (
          <div className="mt-3 grid gap-2 sm:grid-cols-[10rem_minmax(0,1fr)]">
            <input
              value={apiId}
              onChange={(e) => setApiId(e.target.value)}
              placeholder="api_id"
              inputMode="numeric"
              autoComplete="off"
              aria-label="api_id"
              className={monoInputCls}
            />
            <input
              value={apiHash}
              onChange={(e) => setApiHash(e.target.value)}
              placeholder="api_hash"
              autoComplete="off"
              aria-label="api_hash"
              className={monoInputCls}
            />
          </div>
        ) : (
          <p className="mt-3 rounded-btn border border-amber-500/40 bg-amber-500/10 px-2.5 py-2 text-[11.5px] leading-relaxed text-amber-700">
            {t("Ввести ключи может только владелец или администратор Hub. Попросите его открыть этот же мастер — дальше вы подключитесь по QR сами.")}
          </p>
        )}
        {error ? (
          <p className="mt-3 flex items-start gap-1.5 rounded-btn border border-red-400/40 bg-red-500/10 px-2.5 py-2 text-[11.5px] leading-relaxed text-red-600">
            <AlertCircle className="mt-0.5 size-3.5 flex-none" aria-hidden />
            {error}
          </p>
        ) : null}
        {app.canEdit ? (
          <button
            type="button"
            onClick={() => void save()}
            disabled={!valid || saving}
            className="mt-3 inline-flex h-10 cursor-pointer items-center justify-center gap-1.5 rounded-btn bg-primary px-4 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : null}
            {t("Сохранить и перейти к QR-коду")}
          </button>
        ) : null}
        <Hint icon={<ShieldCheck className="size-3.5" />}>{t("Ключи хранятся только на сервере Hub и в интерфейсе больше не показываются.")}</Hint>
      </div>
      <div className="flex min-w-0 flex-col gap-3 border-t border-border bg-background p-4 lg:border-s lg:border-t-0 md:p-5">
        <SideCard title={t("Почему так")}>
          <ul className="mt-2 flex flex-col gap-1.5">
            {[t("WhatsApp подключается без регистрации — шлюз работает как WhatsApp Web"), t("Telegram выдаёт ключи приложению, а не человеку"), t("Регистрация одна на компанию, продавцы её не видят")].map((item) => (
              <li key={item} className="flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
                <Check className="mt-0.5 size-3 flex-none text-primary" strokeWidth={3} aria-hidden />
                {item}
              </li>
            ))}
          </ul>
        </SideCard>
      </div>
    </div>
  );
}

function TelegramQr({ onDone, existing }: PanelProps & { existing?: Channel | null }) {
  const [channel, setChannel] = useState<Channel | null>(existing ?? null);
  const [state, setState] = useState<GatewayState | null>(existing?.state ?? null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const done = useRef(false);

  // создать канал и запустить сессию шлюза (или переподключить существующий)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ch = existing ? await channelsApi.reconnect(existing.id) : await channelsApi.create("telegram");
        if (cancelled) return;
        setChannel(ch);
        setState(ch.state);
      } catch (err) {
        if (!cancelled) setError(apiErrorText(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [existing]);

  // живое состояние: событие channel.state + опрос (QR обновляется самим Telegram примерно раз в полминуты)
  useEffect(() => {
    if (!channel) return undefined;
    const apply = (ch: Channel) => {
      setChannel(ch);
      if (ch.state) setState(ch.state);
    };
    const off = onServerEvent("channel.state", (data) => {
      const d = data as { id: string; status: Channel["status"]; phone: string | null; name: string | null; state: GatewayState };
      if (d.id !== channel.id) return;
      setState((prev) => ({ ...(prev ?? { state: "connecting" }), ...d.state }));
      setChannel((prev) => (prev ? { ...prev, status: d.status, phone: d.phone ?? prev.phone, name: d.name || prev.name, accountName: d.name || prev.accountName } : prev));
    });
    const poll = setInterval(() => {
      channelsApi.get(channel.id).then(apply).catch(() => undefined);
    }, 2000);
    return () => {
      off();
      clearInterval(poll);
    };
  }, [channel?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSeconds(30);
    const id = setInterval(() => setSeconds((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [state?.qr]);

  const finish = useCallback(
    (ch: Channel) => {
      if (done.current) return;
      done.current = true;
      onDone(ch.name || ch.phone || "Telegram", [
        { label: t("Тип"), value: t("Telegram · аккаунт") },
        { label: t("Способ входа"), value: t("QR-код") },
        { label: t("Номер"), value: ch.phone || "—" },
        { label: t("Профиль"), value: ch.accountName || ch.name || "—" },
        { label: t("История"), value: t("за {days} дн.", { days: ch.settings.historyDays }) },
      ]);
    },
    [onDone],
  );

  useEffect(() => {
    if (channel && (channel.status === "connected" || state?.state === "open")) finish(channel);
  }, [channel, state?.state, finish]);

  const sendPassword = async () => {
    if (!channel || !password) return;
    setSending(true);
    setError("");
    try {
      const ch = await channelsApi.password(channel.id, password);
      setChannel(ch);
      setState(ch.state);
      setPassword("");
    } catch (err) {
      setError(apiErrorText(err));
    } finally {
      setSending(false);
    }
  };

  const reconnect = async () => {
    if (!channel) return;
    setError("");
    try {
      const ch = await channelsApi.reconnect(channel.id);
      setChannel(ch);
      setState(ch.state);
    } catch (err) {
      setError(apiErrorText(err));
    }
  };

  const copyLink = async () => {
    if (!state?.qr) return;
    try {
      await navigator.clipboard.writeText(state.qr);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const gw = state?.state;
  const waiting = !gw || gw === "starting" || gw === "connecting";
  const gatewayDown = gw === "gateway_down";
  const needPassword = gw === "password";
  const badPassword = needPassword && state?.error === "bad_password";
  const active = needPassword ? 3 : 2;

  return (
    <div className="grid flex-1 overflow-y-auto lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="min-w-0 p-4 md:p-5">
        <Steps
          active={active}
          items={[t("Откройте Telegram на телефоне"), t("Настройки → Устройства → Подключить устройство"), t("Наведите камеру на код справа")]}
        />

        <div className="mt-4 rounded-btn border border-border bg-background p-3">
          <p className="text-[11.5px] leading-relaxed text-muted-foreground">
            {t("Подключается любой аккаунт Telegram — с возможностью писать первым. Код из SMS не нужен: вход подтверждают на телефоне. Если включена двухэтапная проверка, Telegram попросит облачный пароль — введите его здесь.")}
          </p>
          <p className="mt-2 text-[11.5px] leading-relaxed text-muted-foreground">
            {t("После подключения новые сообщения пойдут во «Входящие», подтянется переписка за последние {days} дней. Сообщения, отправленные с самого телефона, тоже попадут в Hub.", {
              days: channel?.settings.historyDays ?? 30,
            })}
          </p>
        </div>

        {needPassword ? (
          <div className="mt-3 rounded-btn border border-amber-500/40 bg-amber-500/10 p-3">
            <p className="flex items-center gap-1.5 text-[12px] font-semibold text-amber-700">
              <KeyRound className="size-3.5" aria-hidden />
              {t("Код отсканирован — нужен облачный пароль Telegram")}
            </p>
            <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
              {t("Это пароль двухэтапной проверки из настроек Telegram (не код из SMS). Hub его не сохраняет — он уходит прямо в Telegram.")}
            </p>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void sendPassword();
                }}
                placeholder={t("Облачный пароль")}
                autoComplete="off"
                aria-label={t("Облачный пароль")}
                className={inputCls}
              />
              <button
                type="button"
                onClick={() => void sendPassword()}
                disabled={!password || sending}
                className="inline-flex h-10 flex-none cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sending ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : null}
                {t("Войти")}
              </button>
            </div>
            {badPassword ? <p className="mt-1.5 text-[11.5px] text-red-600">{t("Пароль не подошёл — попробуйте ещё раз.")}</p> : null}
          </div>
        ) : null}

        {error ? (
          <p className="mt-3 flex items-start gap-1.5 rounded-btn border border-red-400/40 bg-red-500/10 px-2.5 py-2 text-[11.5px] leading-relaxed text-red-600">
            <AlertCircle className="mt-0.5 size-3.5 flex-none" aria-hidden />
            {error}
          </p>
        ) : null}
        {gatewayDown ? (
          <p className="mt-3 flex items-start gap-1.5 rounded-btn border border-red-400/40 bg-red-500/10 px-2.5 py-2 text-[11.5px] leading-relaxed text-red-600">
            <AlertCircle className="mt-0.5 size-3.5 flex-none" aria-hidden />
            {t("Шлюз Telegram не отвечает. Попробуйте через минуту или сообщите администратору.")}
          </p>
        ) : null}
        {gw === "logged_out" || gw === "closed" || gw === "failed" ? (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-btn border border-border bg-background px-2.5 py-2 text-[11.5px]">
            <span className="text-muted-foreground">
              {gw === "failed"
                ? state?.error === "not_configured"
                  ? t("Ключи приложения Telegram ещё не введены — закройте мастер и откройте его снова: появится шаг регистрации.")
                  : t("Сессия не поднялась{error}.", { error: state?.error ? `: ${state.error}` : "" })
                : t("Сессия завершена.")}
            </span>
            <button type="button" onClick={reconnect} className="inline-flex cursor-pointer items-center gap-1 font-semibold text-primary hover:brightness-110">
              <RefreshCw className="size-3" aria-hidden />
              {t("Попробовать снова")}
            </button>
          </div>
        ) : null}

        <Hint icon={<ShieldCheck className="size-3.5" />}>{t("Аккаунт видит только администратор. Продавцы получают диалоги, а не доступы.")}</Hint>
      </div>

      {/* правая колонка: сам QR-код и статус */}
      <div className="flex min-w-0 flex-col gap-3 border-t border-border bg-background p-4 lg:border-s lg:border-t-0 md:p-5">
        <div className="flex flex-col items-center">
          <div className="relative w-full max-w-[15rem] rounded-card border border-border bg-background p-3">
            <div className="grid aspect-square w-full place-items-center overflow-hidden rounded-btn bg-white p-2">
              {state?.qr ? (
                <QRCodeSVG value={state.qr} size={220} level="M" bgColor="#ffffff" fgColor="#111111" className="size-full" />
              ) : (
                <div className="text-center text-[#333]">
                  <Loader2 className="mx-auto size-6 animate-spin text-[#666]" aria-hidden />
                  <p className="mt-2 text-[11px]">{gatewayDown ? t("Шлюз недоступен") : needPassword ? t("Ждём пароль") : t("Получаем код…")}</p>
                </div>
              )}
            </div>
            {needPassword || (gw === "connecting" && !state?.qr) ? (
              <div className="absolute inset-3 grid place-items-center rounded-btn bg-background/90 backdrop-blur-sm">
                <div className="text-center">
                  <span className="mx-auto grid size-11 place-items-center rounded-full bg-emerald-500 text-white" aria-hidden>
                    <Smartphone className="size-5" />
                  </span>
                  <p className="mt-2 text-[12px] font-semibold">{t("Код отсканирован")}</p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">{needPassword ? t("Введите облачный пароль слева") : t("Подтвердите на телефоне")}</p>
                </div>
              </div>
            ) : null}
          </div>

          <div className="mt-3 flex w-full items-center justify-between gap-2 rounded-btn border border-border bg-background px-3 py-2">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
              <Loader2 className={cn("size-3 text-primary", (waiting || state?.qr) && "animate-spin")} aria-hidden />
              {state?.qr ? t("Ждём сканирование · {seconds} с", { seconds }) : waiting ? t("Соединяемся с Telegram…") : needPassword ? t("Ждём пароль") : t("Код не выдан")}
            </span>
            <button
              type="button"
              onClick={copyLink}
              disabled={!state?.qr}
              className="inline-flex flex-none cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn px-1.5 py-1 text-[11px] font-semibold text-primary transition hover:bg-primary/10 disabled:opacity-40"
            >
              {copied ? <Check className="size-3" strokeWidth={3} aria-hidden /> : <Copy className="size-3" aria-hidden />}
              {copied ? t("Ссылка скопирована") : t("Скопировать ссылку на код")}
            </button>
          </div>
          <p className="mt-2 text-center text-[10px] leading-relaxed text-muted-foreground">
            {t("Канал:")} {channel?.id.slice(0, 8) ?? t("создаётся…")}
          </p>
        </div>

        <SideCard title={t("Что будет после входа")}>
          <ul className="mt-2 flex flex-col gap-1.5">
            {[t("Все новые переписки → в CRM"), t("Подтянутся имя профиля и номер"), t("Файлы и фото — в обе стороны")].map((item) => (
              <li key={item} className="flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
                <Check className="mt-0.5 size-3 flex-none text-primary" strokeWidth={3} aria-hidden />
                {item}
              </li>
            ))}
          </ul>
        </SideCard>
      </div>
    </div>
  );
}
